import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, List, Tag, Spin, message, Empty } from 'antd';
import { 
  ProjectOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  FileTextOutlined,
  MessageOutlined,
  ReloadOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { ShareManager } from '../../components/ShareManager';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { CitadeloLayout } from '../../components/CitadeloLayout';

const { Title, Text } = Typography;

interface ProjectType {
  id: number;
  name: string;
  zohoValue?: string;
  category?: string;
}

interface Project {
  id: number;
  pid: string;
  projectName?: string;
  client?: {
    name: string;
    contactFirstName?: string;
    contactLastName?: string;
    contactName?: string; // legacy field
  };
  projectTypes?: ProjectType[];
  customProjectType?: string;
  status?: string;
  projectStatus?: string;
  startDate?: string;
  endDate?: string;
  scope: string;
  nextcloudUploadLink?: string;
  nextcloudSharePassword?: string;
  nextcloudShareExpiration?: string;
}

const statusColors: Record<string, string> = {
  proposal: 'gold',
  obtaining: 'default', // legacy
  preparing: 'processing',
  scheduled: 'cyan',
  testing: 'warning',
  reporting: 'processing',
  follow_up: 'success',
  completed: 'success'
};

const statusLabels: Record<string, string> = {
  proposal: 'Proposal',
  obtaining: 'Obtaining', // legacy
  preparing: 'Preparing',
  scheduled: 'Scheduled',
  testing: 'Testing',
  reporting: 'Reporting',
  follow_up: 'Follow-up',
  completed: 'Completed'
};

// Helper function to get display name for project types
const getProjectTypesDisplay = (project: Project): string[] => {
  const types: string[] = [];
  if (project.projectTypes && project.projectTypes.length > 0) {
    types.push(...project.projectTypes.map(pt => pt.name));
  }
  if (project.customProjectType) {
    types.push(project.customProjectType);
  }
  return types.length > 0 ? types : ['Not specified'];
};

// Helper to get contact name
const getContactName = (client?: Project['client']): string => {
  if (!client) return '';
  if (client.contactFirstName || client.contactLastName) {
    return `${client.contactFirstName || ''} ${client.contactLastName || ''}`.trim();
  }
  return client.contactName || '';
};

export const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjects();
      setProjects(response.data);
    } catch (error) {
      message.error('Failed to load projects');
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectActions = (project: Project) => {
    const actions = [];
    const status = project.status || project.projectStatus; // Handle both field names

    // Requirements checklist - available for all statuses
    actions.push({
      label: 'Requirements Checklist',
      icon: <FileTextOutlined />,
      onClick: () => navigate(`/project/${project.id}/checklist`, { state: { project } }),
      type: 'default' as const
    });

    // Distribution management - available for all statuses
    actions.push({
      label: 'Manage Distribution',
      icon: <TeamOutlined />,
      onClick: () => navigate(`/project/${project.id}/distribution`, { state: { project } }),
      type: 'default' as const
    });

    // Feedback action - available for completed status
    if (status === 'completed') {
      actions.push({
        label: 'Submit Feedback',
        icon: <MessageOutlined />,
        onClick: () => navigate(`/project/${project.id}/feedback`, { state: { project } }),
        type: 'default' as const
      });
    }

    // Retest action - available for completed status
    if (status === 'completed') {
      actions.push({
        label: 'Request Retest',
        icon: <ReloadOutlined />,
        onClick: () => navigate(`/project/${project.id}/retest`, { state: { project } }),
        type: 'default' as const
      });
    }

    return actions;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <CitadeloLayout showHeader={false}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          <Card style={{ 
            textAlign: 'center', 
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(26, 26, 46, 0.1)'
          }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text style={{ color: 'var(--citadelo-text)' }}>Loading your projects...</Text>
            </div>
          </Card>
        </div>
      </CitadeloLayout>
    );
  }

  return (
    <CitadeloLayout onRefresh={fetchProjects} onLogout={logout}>
      {/* Welcome Header */}
      <Card style={{ 
        marginBottom: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(26, 26, 46, 0.1)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={2} style={{ color: 'var(--citadelo-primary)', marginBottom: '8px' }}>
            <ProjectOutlined style={{ marginRight: '8px', color: 'var(--citadelo-accent)' }} />
            Your Projects
          </Title>
          <Text style={{ color: 'var(--citadelo-text-light)', fontSize: '16px' }}>
            Welcome back, {user?.username}! Manage your penetration testing projects.
          </Text>
        </div>
      </Card>

      {/* Projects List */}
      {projects.length === 0 ? (
        <Card style={{
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(26, 26, 46, 0.1)'
        }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No projects found"
          >
            <Text style={{ color: 'var(--citadelo-text-light)' }}>
              You don't have any projects assigned yet. Contact your project manager for access.
            </Text>
          </Empty>
        </Card>
      ) : (
        <div style={{ overflow: 'hidden' }}>
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
          dataSource={projects}
          renderItem={(project) => (
            <List.Item>
                <Card
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(26, 26, 46, 0.1)',
                  border: '1px solid var(--citadelo-border)'
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span 
                      style={{ color: 'var(--citadelo-primary)', fontWeight: '600', cursor: 'pointer' }}
                      onClick={() => navigate(`/project/${project.id}/status`, { state: { project } })}
                    >
                      {project.projectName || project.pid}
                    </span>
                    <Tag color={statusColors[project.status || project.projectStatus || 'proposal']}>
                      {statusLabels[project.status || project.projectStatus || 'proposal']}
                    </Tag>
                  </div>
                }
              >
                {/* Project Types */}
                <div style={{ marginBottom: '12px' }}>
                  {getProjectTypesDisplay(project).slice(0, 3).map((type, idx) => (
                    <Tag key={idx} color="blue" style={{ marginBottom: '4px' }}>
                      {type}
                    </Tag>
                  ))}
                  {getProjectTypesDisplay(project).length > 3 && (
                    <Tag color="default">+{getProjectTypesDisplay(project).length - 3} more</Tag>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <Text strong style={{ color: 'var(--citadelo-primary)' }}>{project.client?.name}</Text>
                  <br />
                  <Text style={{ color: 'var(--citadelo-text-light)' }}>
                    <UserOutlined style={{ marginRight: '4px', color: 'var(--citadelo-accent)' }} />
                    {getContactName(project.client)}
                  </Text>
                  <br />
                  <Text style={{ color: 'var(--citadelo-text-light)', fontSize: '12px' }}>
                    PID: {project.pid}
                  </Text>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <Text style={{ color: 'var(--citadelo-text-light)' }}>
                    <CalendarOutlined style={{ marginRight: '4px', color: 'var(--citadelo-accent)' }} />
                    Start: {formatDate(project.startDate)}
                  </Text>
                  <br />
                  <Text style={{ color: 'var(--citadelo-text-light)' }}>
                    <CalendarOutlined style={{ marginRight: '4px', color: 'var(--citadelo-accent)' }} />
                    End: {formatDate(project.endDate)}
                  </Text>
                </div>

                <div style={{ 
                  maxHeight: '60px', 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '16px'
                }}>
                  <Text style={{ color: 'var(--citadelo-text-light)', fontSize: '12px' }}>
                    {project.scope ? `${project.scope.replace(/<[^>]*>/g, '').substring(0, 100)}...` : 'No scope defined'}
                  </Text>
                </div>

                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px',
                  borderTop: '1px solid var(--citadelo-border)',
                  paddingTop: '16px'
                }}>
                  {getProjectActions(project).map((action, index) => (
                    <Button
                      key={index}
                      type={action.type}
                      icon={action.icon}
                      onClick={action.onClick}
                      size="small"
                      style={{
                        borderRadius: '6px',
                        fontWeight: '500'
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                  <ShareManager 
                    record={project} 
                    onUpdate={fetchProjects} 
                    buttonType="default" 
                    size="small"
                    style={{
                      borderRadius: '6px',
                      fontWeight: '500'
                    }}
                  />
                </div>
              </Card>
            </List.Item>
          )}
        />
        </div>
      )}
    </CitadeloLayout>
  );
};
